import { useState } from 'react'
import type { RegistryUpstream } from '@/lib/apiTypes'
import { manageProvider, patchProviderArtefact } from '@/lib/hooks'
import { Modal } from '@/shared/components/Modal'
import { RegistryIcon } from '@/shared/components/RepoProviderIcon'

interface Props {
  orgId: string
  onClose: () => void
  /** Fired after a successful save with the identity the worker stored (registry + lowercased
   *  namespace/type) - lets the caller kick the targeted stats refresh for exactly this row. */
  onCreated: (upstream: RegistryUpstream, namespace: string, type: string) => void
  /**
   * Edit mode: seed from an existing row. Registry, namespace and type are locked (together they
   * identify the artefact - the same provider may be held once per registry) and only the
   * constraint is editable (PATCH - the worker re-populates and re-enqueues a scan when the
   * config actually changed, the same flow adding a provider fires). The registry is shown
   * read-only; serving the provider from the other registry means adding it again.
   */
  initial?: {
    upstream: RegistryUpstream
    namespace: string
    type: string
    version_constraint: string
  }
}

/** Human label for a registry host. */
function registryLabel(upstream: RegistryUpstream): string {
  return upstream === 'opentofu.org' ? 'registry.opentofu.org' : 'registry.terraform.io'
}

/**
 * Add / edit a provider. Adding (POST /api/orgs/:orgId/providers) is for a provider nothing in
 * the org references yet - anything a module or lockfile names is auto-discovered and appears on
 * its own. Adding serves it (upstream versions plus, on Business+, terramantle's own
 * versions); which versions are upstream vs self-built is a per-version fact, so this form only
 * captures the identity + config. The registry is part of that identity: an explicit choice is
 * required (no default - terraform.io and opentofu.org are separate lineages with different
 * bytes, so silently defaulting would key the wrong one). With `initial` it is the Edit form for
 * an existing row.
 */
export function ManageProviderModal({ orgId, onClose, onCreated, initial }: Props) {
  const editing = !!initial
  const [ns, setNs] = useState(initial?.namespace ?? '')
  const [type, setType] = useState(initial?.type ?? '')
  // Add mode starts with NO registry selected; edit mode carries the row's (immutable) registry.
  const [upstream, setUpstream] = useState<RegistryUpstream | ''>(initial?.upstream ?? '')
  const [constraint, setConstraint] = useState(initial?.version_constraint ?? '')
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<{ ns?: string; type?: string; upstream?: string; api?: string }>({})

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const newErrors: typeof errors = {}
    if (!ns.trim()) newErrors.ns = 'Namespace is required.'
    if (!type.trim()) newErrors.type = 'Type is required.'
    if (!upstream) newErrors.upstream = 'Choose the registry this provider comes from.'
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setBusy(true)
    setErrors({})
    const registry = upstream as RegistryUpstream // validated non-empty above
    try {
      if (editing) {
        // The registry is the route segment, never the body: the worker rejects `upstream` in a
        // PATCH because it is part of the identity.
        await patchProviderArtefact(orgId, registry, ns.trim(), type.trim(), { version_constraint: constraint.trim() })
      } else {
        await manageProvider(orgId, { namespace: ns.trim(), type: type.trim(), upstream: registry, version_constraint: constraint.trim() })
      }
      onCreated(registry, ns.trim().toLowerCase(), type.trim().toLowerCase())
      onClose()
    } catch (err) {
      setErrors({ api: (err as Error).message ?? (editing ? 'Failed to save provider' : 'Failed to add provider') })
    } finally {
      setBusy(false)
    }
  }

  const title = editing ? `Edit ${initial!.namespace}/${initial!.type}` : 'Add provider'

  return (
    <Modal track="manage_provider" title={title} onClose={onClose} maxWidth={420}>
      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
        <div>
          <label className="field-label">Namespace</label>
          <input
            className="input"
            placeholder="hashicorp"
            value={ns}
            onChange={(e) => { setNs(e.target.value); setErrors({ ...errors, ns: undefined }) }}
            disabled={editing}
            autoFocus={!editing}
          />
          {errors.ns && <div style={fieldError}>{errors.ns}</div>}
        </div>

        <div>
          <label className="field-label">Type</label>
          <input
            className="input"
            placeholder="aws"
            value={type}
            onChange={(e) => { setType(e.target.value); setErrors({ ...errors, type: undefined }) }}
            disabled={editing}
          />
          {errors.type && <div style={fieldError}>{errors.type}</div>}
          <div style={lockedCaption}>
            {editing
              ? 'Registry, namespace and type identify the provider - only the constraint is editable. Newly matched versions are scanned automatically.'
              : 'Providers your modules and workspaces already reference are discovered automatically - add one here only when nothing references it yet.'}
          </div>
        </div>

        <div>
          <label className="field-label">Registry</label>
          {editing ? (
            <>
              <div
                className="input"
                aria-readonly
                style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text2)', cursor: 'default' }}
              >
                <RegistryIcon upstream={initial!.upstream} />
                <span>{registryLabel(initial!.upstream)}</span>
              </div>
              <div style={lockedCaption}>
                Registry is part of the provider's identity. To serve the same provider from the other registry, add it again.
              </div>
            </>
          ) : (
            <>
              <select
                className="input"
                value={upstream}
                onChange={(e) => { setUpstream(e.target.value as RegistryUpstream | ''); setErrors({ ...errors, upstream: undefined }) }}
                aria-invalid={!!errors.upstream}
              >
                <option value="" disabled>Select a registry…</option>
                <option value="terraform.io">registry.terraform.io</option>
                <option value="opentofu.org">registry.opentofu.org</option>
              </select>
              {errors.upstream && <div style={fieldError}>{errors.upstream}</div>}
              <div style={lockedCaption}>
                registry.terraform.io and registry.opentofu.org publish different builds of the same provider. The registry is part of the provider's identity: to serve it from both, add it once per registry.
              </div>
            </>
          )}
        </div>

        <div>
          <label className="field-label">Version constraint</label>
          <input
            className="input"
            placeholder="~> 5.0 (leave blank for all versions)"
            value={constraint}
            onChange={(e) => setConstraint(e.target.value)}
            autoFocus={editing}
          />
        </div>

        {errors.api && <div className="feedback-error">{errors.api}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || (!editing && !upstream)}>
            {busy ? <span className="spinner" /> : editing ? 'Save changes' : 'Add provider'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

const fieldError: React.CSSProperties = {
  marginTop: 4,
  fontSize: '0.72rem',
  color: 'var(--red)',
}

const lockedCaption: React.CSSProperties = {
  marginTop: 6,
  fontSize: '0.7rem',
  color: 'var(--text3)',
}
